import { useState, useCallback } from 'react';
// import { HolderOutlined } from '@ant-design/icons';
import { Button, Space } from 'antd';
import { arrayMoveImmutable } from 'array-move';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { sortTopicsApi } from '@services/Selection';
import './SortTopic.less';

/** 拖拽手柄 */
function DragHandleButton({ listeners }) {
    return (
        <Button type="text" size="small" style={{ userSelect: 'none', WebkitUserSelect: 'none' }} {...listeners}>
            <i className="bi bi-grip-vertical" />
        </Button>
    );
}

/** 二级主题行（子拖拽项） */
function SortableChildRow({ childData }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: childData.id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.65 : undefined,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} className="topics_wrap topics_child_item_wrap">
            <Space>
                <DragHandleButton listeners={listeners} />
            </Space>
            <span>{childData.name || childData.title}</span>
        </div>
    );
}

/** 二级主题拖拽区域 */
function SortableChildList({ parentId, childItems, dndSensors, onDragEndChild }) {
    const ids = (childItems || []).map(c => c.id);
    if (!childItems?.length) {
        return null;
    }
    return (
        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onDragEndChild(parentId)}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                <div className="child_container_wrap">
                    {childItems.map(child => (
                        <SortableChildRow key={child.id} childData={child} />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
}

/** 一级主题行（主拖拽项） */
function SortableMainRow({ itemData, dndSensors, onDragEndChild }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: itemData.id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.65 : undefined,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} className="topics_item_wrap">
            <div className="topics_wrap">
                <Space>
                    <DragHandleButton listeners={listeners} />
                </Space>
                <span>{itemData.name}</span>
            </div>
            <SortableChildList parentId={itemData.id} childItems={itemData.children} dndSensors={dndSensors} onDragEndChild={onDragEndChild} />
        </div>
    );
}

const SortTopic = ({ tableData, onReset }) => {
    const [data, setData] = useState(tableData);

    const dndSensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 10 },
        }),
    );

    // 一级主题拖拽结束
    const onDragEndMain = useCallback(
        event => {
            const { active, over } = event;
            if (!over || active.id === over.id) {
                return;
            }
            const oldIndex = data.findIndex(it => it.id === active.id);
            const newIndex = data.findIndex(it => it.id === over.id);
            if (oldIndex === -1 || newIndex === -1) {
                return;
            }
            const newArr = arrayMoveImmutable(data, oldIndex, newIndex);
            setData(newArr);
            sortTopicsApi(newArr).then(res => {
                if (res.status_code === 200) {
                    onReset();
                }
            });
        },
        [data, onReset],
    );

    // 二级主题拖拽结束
    const onDragEndChild = useCallback(
        parentId => event => {
            const { active, over } = event;
            if (!over || active.id === over.id) {
                return;
            }
            const newData = [...data];
            const ind = newData.findIndex(it => it.id === parentId);
            if (ind === -1) {
                return;
            }
            const items = newData[ind].children;
            if (!items?.length) {
                return;
            }
            const oldIndex = items.findIndex(s => s.id === active.id);
            const newIndex = items.findIndex(s => s.id === over.id);
            if (oldIndex === -1 || newIndex === -1) {
                return;
            }
            newData[ind] = {
                ...newData[ind],
                children: arrayMoveImmutable(items, oldIndex, newIndex),
            };
            setData(newData);
            sortTopicsApi(newData).then(res => {
                if (res.status_code === 200) {
                    onReset();
                }
            });
        },
        [data, onReset],
    );

    const mainIds = data.map(item => item.id);

    return (
        <div>
            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onDragEndMain}>
                <SortableContext items={mainIds} strategy={verticalListSortingStrategy}>
                    <div className="main_content_wrap">
                        {data.map(item => (
                            <SortableMainRow key={item.id} itemData={item} dndSensors={dndSensors} onDragEndChild={onDragEndChild} />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    );
};

export default SortTopic;
